import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import api from '../services/api';

export default function Upload() {
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, upload: null });
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' });

  const loadHistory = useCallback(async () => {
    try {
      const response = await api.get('/upload/history');
      setUploads(response.data.uploads || []);
    } catch {
      // Silently fail — history might be empty
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const uploadSingleFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      return { filename: file.name, success: false, error: 'Only .xlsx files are accepted' };
    }
    if (file.size > 10 * 1024 * 1024) {
      return { filename: file.name, success: false, error: 'File is too large (max 10MB)' };
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { filename: file.name, success: true, data: response.data };
    } catch (err) {
      let errorMsg = 'Upload failed';
      if (err.response) {
        errorMsg = err.response.data?.error || `Upload failed (HTTP ${err.response.status})`;
      } else if (err.request) {
        errorMsg = 'Cannot reach the server. Make sure the backend is running on port 5001.';
      } else {
        errorMsg = 'Upload failed: ' + err.message;
      }
      return { filename: file.name, success: false, error: errorMsg };
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;

    const fileList = Array.from(files).filter((f) => f.name.toLowerCase().endsWith('.xlsx'));
    if (fileList.length === 0) {
      setError('No .xlsx files found. Only .xlsx files are accepted.');
      return;
    }

    setUploading(true);
    setError('');
    setResults([]);
    setUploadProgress({ current: 0, total: fileList.length, filename: '' });

    const allResults = [];
    for (let i = 0; i < fileList.length; i++) {
      setUploadProgress({ current: i + 1, total: fileList.length, filename: fileList[i].name });
      const result = await uploadSingleFile(fileList[i]);
      allResults.push(result);
    }

    setResults(allResults);
    setUploading(false);
    setUploadProgress({ current: 0, total: 0, filename: '' });
    loadHistory();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileInput = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleDelete = async () => {
    const upload = deleteDialog.upload;
    setDeleteDialog({ open: false, upload: null });

    try {
      await api.delete(`/upload/${upload.id}`);
      loadHistory();
      setResults([]);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const successCount = results.filter((r) => r.success).length;
  const failCount = results.filter((r) => !r.success).length;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Upload Budget Files
      </Typography>

      {/* Drop zone */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 6,
              textAlign: 'center',
              backgroundColor: dragOver ? 'action.hover' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.light',
                backgroundColor: 'action.hover',
              },
            }}
            onClick={() => document.getElementById('file-input').click()}
          >
            {uploading ? (
              <>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography fontWeight={600}>
                  Processing file {uploadProgress.current} of {uploadProgress.total}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {uploadProgress.filename}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(uploadProgress.current / uploadProgress.total) * 100}
                  sx={{ mt: 1, maxWidth: 400, mx: 'auto' }}
                />
              </>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 56, color: 'primary.light', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Drag & drop your .xlsx budget files here
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  or click to select files — you can upload multiple at once
                </Typography>
                <Button variant="outlined" startIcon={<InsertDriveFileIcon />}>
                  Select Files
                </Button>
              </>
            )}
          </Box>
          <input
            id="file-input"
            type="file"
            accept=".xlsx"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
        </CardContent>
      </Card>

      {/* Results */}
      {results.length > 0 && (
        <Alert
          severity={failCount === 0 ? 'success' : successCount > 0 ? 'warning' : 'error'}
          icon={failCount === 0 ? <CheckCircleIcon /> : undefined}
          sx={{ mb: 3 }}
          onClose={() => setResults([])}
        >
          <Typography fontWeight={600} sx={{ mb: 1 }}>
            {successCount} of {results.length} file{results.length !== 1 ? 's' : ''} uploaded successfully
            {failCount > 0 && ` (${failCount} failed)`}
          </Typography>
          <List dense disablePadding>
            {results.map((r, i) => (
              <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {r.success
                    ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    : <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={
                    r.success
                      ? `${r.filename} — ${r.data.transactions_imported} transactions, ${r.data.budget_items_imported || 0} budget items, ${r.data.savings_accounts_imported || 0} savings accounts${r.data.validation_flags > 0 ? `, ${r.data.validation_flags} flags` : ''}`
                      : `${r.filename} — ${r.error}`
                  }
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: r.success ? 'text.primary' : 'error.main',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {/* Typo warnings */}
      {results.some((r) => r.success && r.data.typo_warnings?.length > 0) && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography fontWeight={600} sx={{ mb: 1 }}>
            Potential category name typos detected
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The following category names are similar to existing ones — they may be typos
            in your Excel files. Fix them in the source file and re-upload to avoid
            duplicate categories in your budget.
          </Typography>
          <List dense disablePadding>
            {results
              .filter((r) => r.success && r.data.typo_warnings?.length > 0)
              .flatMap((r) => r.data.typo_warnings)
              .map((tw, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`"${tw.incoming_category}" vs "${tw.existing_category}" (${Math.round(tw.similarity * 100)}% similar)`}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
          </List>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Upload History */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Upload History
      </Typography>

      {loadingHistory ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : uploads.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No files uploaded yet. Upload your first Excel budget file above.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Filename</TableCell>
                <TableCell>Month</TableCell>
                <TableCell align="right">Trans.</TableCell>
                <TableCell align="right">Budget</TableCell>
                <TableCell align="right">Savings</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map((upload) => (
                <TableRow key={upload.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight={500}>
                        {upload.filename}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{upload.file_month || '—'}</TableCell>
                  <TableCell align="right">{upload.records_imported ?? '—'}</TableCell>
                  <TableCell align="right">{upload.budget_items_imported ?? '—'}</TableCell>
                  <TableCell align="right">{upload.savings_accounts_imported ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={upload.status}
                      color={upload.status === 'success' ? 'success' : 'error'}
                      icon={upload.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    {upload.uploaded_at
                      ? new Date(upload.uploaded_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, upload })}
                      title="Delete upload and its transactions"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, upload: null })}
      >
        <DialogTitle>Delete Upload?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete "{deleteDialog.upload?.filename}" and all
            {' '}{deleteDialog.upload?.records_imported} imported transactions.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, upload: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
